/* eslint-disable react/require-default-props */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import Modal from '../../../../components/Modal/Modal';
import { GhostButton, PrimaryButton } from '../../../../styles/ui/Button';
import { FooterNote, ModalIntro } from './modalKit';
import { routeLabel } from '../utils/format';

// =============================================================================
// Revisão das alterações do pedido — passo 2 do salvar da edição (decisão F3,
// fase 2 da refatoração visual, 2026-09-10).
//
// Existe porque a lista enviada no PUT substitui INTEGRALMENTE o pedido: um
// item que saiu da lista é REMOVIDO no servidor, e o operador precisa ver isso
// antes de confirmar. O diff (utils/editDiff.js) agrupa em quantidade alterada
// · itens incluídos · itens removidos, com de → para e delta assinado.
//
// Quando a redução vai encerrar a ordem (previsão fiel ao contrato — só com
// contagem satisfeita), o modal avisa em âmbar. A autoridade continua sendo o
// `status_transition` da resposta.
// =============================================================================

const GroupHead = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   margin: 14px 0 6px;

   .dot {
      width: 8px;
      height: 8px;
      border-radius: 999px;
      background: ${({ $kind }) => {
         if ($kind === 'add') return '#4098ef';
         if ($kind === 'cut') return '#c62828';
         return '#f59e0b';
      }};
   }
   .ttl {
      font-size: 11px;
      font-weight: 800;
      letter-spacing: 0.05em;
      text-transform: uppercase;
      color: ${({ theme }) => theme.colors.textPrimary};
   }
   .cnt {
      font-size: 11.5px;
      color: ${({ theme }) => theme.colors.textMuted};
   }
`;

const GroupBox = styled.div`
   border: 1px solid ${({ theme }) => theme.colors.border};
   border-radius: 10px;
   overflow: hidden;
`;

const DiffRow = styled.div`
   display: grid;
   grid-template-columns: minmax(0, 1fr) 90px 90px 70px;
   gap: 12px;
   align-items: center;
   padding: 11px 12px;
   border-top: 1px solid ${({ theme }) => theme.colors.borderLight};

   &:first-child {
      border-top: none;
   }
   &:nth-child(even) {
      background: #fafafb;
   }

   .name {
      min-width: 0;
      font-size: 13px;
      font-weight: 600;
      color: ${({ theme }) => theme.colors.textPrimary};
      overflow-wrap: anywhere;
   }
   .sku {
      display: block;
      margin-top: 1px;
      font-size: 11px;
      font-weight: 400;
      color: ${({ theme }) => theme.colors.textMuted};
   }
   .from {
      font-size: 12.5px;
      text-align: right;
      white-space: nowrap;
      color: ${({ theme }) => theme.colors.textMuted};
   }
   .to {
      font-size: 12.5px;
      font-weight: 800;
      text-align: right;
      white-space: nowrap;
      color: ${({ theme }) => theme.colors.textPrimary};
   }
`;

const Delta = styled.span`
   font-size: 12px;
   font-weight: 800;
   text-align: right;
   white-space: nowrap;
   font-variant-numeric: tabular-nums;
   color: ${({ $positive }) => ($positive ? '#0b3a63' : '#c62828')};
`;

const TransitionNote = styled.p`
   margin: 14px 0 0;
   padding: 10px 12px;
   border-radius: 8px;
   border: 1px solid #ecd9ab;
   background: #fdf6e3;
   font-size: 12.5px;
   line-height: 1.5;
   font-weight: 600;
   color: #8a5a00;
`;

const GROUPS = [
   { key: 'changed', kind: 'change', title: 'Quantidade alterada' },
   { key: 'added', kind: 'add', title: 'Itens incluídos' },
   { key: 'removed', kind: 'cut', title: 'Itens removidos' },
];

function fmtQty(value) {
   return value == null ? '—' : `${value} un`;
}

export default function ReviewChangesModal({
   isOpen,
   order,
   diff,
   closesOrder = false,
   isSubmitting = false,
   onCancel,
   onConfirm,
}) {
   const total = diff?.total ?? 0;

   return (
      <Modal
         isOpen={isOpen}
         title="Revisar alterações"
         subtitle={
            order ? `Pedido #${order.id} · ${routeLabel(order)}` : null
         }
         onClose={isSubmitting ? undefined : onCancel}
         maxWidth="680px"
         footer={
            <>
               <FooterNote>
                  {total} {total === 1 ? 'alteração' : 'alterações'}
               </FooterNote>
               <GhostButton
                  type="button"
                  onClick={onCancel}
                  disabled={isSubmitting}
               >
                  Voltar e ajustar
               </GhostButton>
               <PrimaryButton
                  type="button"
                  onClick={onConfirm}
                  disabled={isSubmitting || total === 0}
               >
                  {isSubmitting ? 'Salvando…' : 'Salvar alterações'}
               </PrimaryButton>
            </>
         }
      >
         <ModalIntro $tone="info">
            A lista enviada <strong>substitui integralmente</strong> os itens do
            pedido. A reserva na origem é recalculada no salvamento.
         </ModalIntro>

         {GROUPS.map((group) => {
            const rows = diff?.[group.key] ?? [];
            if (rows.length === 0) return null;
            return (
               <React.Fragment key={group.key}>
                  <GroupHead $kind={group.kind}>
                     <span className="dot" aria-hidden="true" />
                     <span className="ttl">{group.title}</span>
                     <span className="cnt">
                        {rows.length} {rows.length === 1 ? 'item' : 'itens'}
                     </span>
                  </GroupHead>
                  <GroupBox>
                     {rows.map((row) => (
                        <DiffRow key={row.prodId}>
                           <span className="name">
                              {row.name ?? `Produto ${row.prodId}`}
                              <span className="sku">
                                 SKU {row.sku ?? '—'}
                              </span>
                           </span>
                           <span className="from">{fmtQty(row.from)}</span>
                           <span className="to">
                              {row.to == null ? 'removido' : fmtQty(row.to)}
                           </span>
                           <Delta $positive={row.delta > 0}>
                              {row.delta > 0 ? `+${row.delta}` : row.delta}
                           </Delta>
                        </DiffRow>
                     ))}
                  </GroupBox>
               </React.Fragment>
            );
         })}

         {closesOrder && (
            <TransitionNote>
               Depois desta redução não sobra nada a expedir — a ordem será
               CONCLUÍDA ao salvar. Não é possível reabri-la pela edição.
            </TransitionNote>
         )}
      </Modal>
   );
}

ReviewChangesModal.propTypes = {
   isOpen: PropTypes.bool.isRequired,
   // eslint-disable-next-line react/forbid-prop-types
   order: PropTypes.object,
   diff: PropTypes.shape({
      // eslint-disable-next-line react/forbid-prop-types
      changed: PropTypes.array,
      // eslint-disable-next-line react/forbid-prop-types
      added: PropTypes.array,
      // eslint-disable-next-line react/forbid-prop-types
      removed: PropTypes.array,
      total: PropTypes.number,
   }),
   closesOrder: PropTypes.bool,
   isSubmitting: PropTypes.bool,
   onCancel: PropTypes.func.isRequired,
   onConfirm: PropTypes.func.isRequired,
};
